import React, { useEffect, useState } from "react"
import { MobergMenuDropdownButton } from "../../../../../Components/MobergMenu/MobergMenuDropdownButton"
import { useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../../../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile"
import { useMobergMenu } from "../../../../../Hooks/useMobergMenu"
import { MedicationJSON } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { useEMRDataProvider } from "../../../../../Providers/EMRDataProvider"
import { toMedicationJSON } from "./Medications"
import { Scrollbar } from "../../../../../Constants/StyledComponents"
import { getColors, MobergColumn, MobergFontSize, MobergHeader, MobergIconSize, MobergMenuItem, MobergRow, MobergTheme } from "../../../../../Moberg"
import { MdCircle } from "react-icons/md"

type HarmonizedMedicationSelectorProps = {
    defaultMedication?: MedicationJSON
    highlight?: boolean
    onChange: (newMedication: MedicationJSON) => void
}

export const HarmonizedMedicationSelector: React.FC<HarmonizedMedicationSelectorProps> = ({ defaultMedication, highlight=true, onChange }) => {
    const { harmonizedMedications } = useEMRDataProvider()
    const { patientMedications } = useRecoilValue(currentPatientFileInfoAtom)
    const medicationSelectionMenu = useMobergMenu({ placement: "bottom", useAnchorAsMinWidth: true })
    const [medicationOptions, setMedicationOptions] = useState<Array<MedicationJSON>>([])
    const drugClasses = [...new Set(medicationOptions.map(m => m.class))]
    const [currentMedication, setCurrentMedication] = useState<MedicationJSON | undefined>(defaultMedication)

    const handleMedicationSelect = (newMedication: MedicationJSON) => {
        setCurrentMedication(newMedication)
        onChange(newMedication)
        setTimeout(() => medicationSelectionMenu.close())
    }

    const shouldHighlight = (medicationId: number) => {
        if (!highlight) {
            return false
        }

        return patientMedications.harmonized.find(m => m.id === medicationId) !== undefined
    }

    useEffect(() => {
        setMedicationOptions(harmonizedMedications.map(toMedicationJSON))
    }, [harmonizedMedications])

     return (
        <div>
            <MobergMenuDropdownButton menu={medicationSelectionMenu} style={{ width: "200px" }}>
                {currentMedication?.rawDataKey ?? "Select an option..."}
            </MobergMenuDropdownButton>

            <medicationSelectionMenu.MobergMenu style={{ padding: "8px 4px" }}>
                <Scrollbar>
                    <MobergRow gap="16px" verticalAlign="start" style={{ maxHeight: "400px" }}>
                        {drugClasses?.map(drugClass => (
                            <MobergColumn gap="4px" key={drugClass}>
                                <MobergHeader style={{ fontSize: MobergFontSize.SMALL, padding: "0 10px" }}>{drugClass.toUpperCase()}</MobergHeader>
                                <MobergColumn>
                                    {medicationOptions.filter(m => m.class === drugClass).map(option => {
                                        const highlight = shouldHighlight(Number(option.id))
                                        return (
                                            <MobergMenuItem
                                                key={option.id}
                                                onClick={() => handleMedicationSelect(option)}
                                                style={{ fontWeight: highlight ? "bold" : "normal", gap: "8px" }}
                                            >
                                                {option.rawDataKey}
                                                {highlight && <MdCircle size={MobergIconSize.XX_SMALL} color={getColors(MobergTheme.BLUE).main} />}
                                            </MobergMenuItem>
                                        )

                                    })}
                                </MobergColumn>
                            </MobergColumn>
                        ))}
                    </MobergRow>
                </Scrollbar>
            </medicationSelectionMenu.MobergMenu>
        </div>
     )

}
